import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { initGA } from '@/lib/analytics';

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    initGA();
    setShowBanner(false);
  };

  const declineCookies = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setShowBanner(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          data-testid="cookie-consent-banner"
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto bg-black border border-white/20 shadow-lg rounded-xl p-4 md:p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-brand-lime/20 rounded-lg shrink-0">
                  <Cookie className="w-5 h-5 text-brand-lime" />
                </div>
                <div>
                  <h3 className="font-black text-white mb-1">
                    We use cookies
                  </h3>
                  <p className="text-sm text-white/70">
                    We use Google Analytics cookies to understand how visitors use our site.
                    No cookies are set unless you click "Accept". See{' '}
                    <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-white underline hover:text-brand-lime transition-colors">
                      how Google uses your data
                    </a>{' '}
                    and our{' '}
                    <a href="/privacy" className="text-white underline hover:text-brand-lime transition-colors">
                      Privacy Policy
                    </a>.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 w-full md:w-auto">
                <button
                  onClick={declineCookies}
                  data-testid="cookie-decline-btn"
                  className="flex-1 md:flex-none px-4 py-2 rounded-full border border-white/30 text-white text-sm font-bold hover:bg-white/10 transition-colors"
                >
                  Decline
                </button>
                <button
                  onClick={acceptCookies}
                  data-testid="cookie-accept-btn"
                  className="flex-1 md:flex-none px-4 py-2 rounded-full bg-brand-lime text-black text-sm font-bold border-2 border-black hover:translate-y-[-2px] transition-transform"
                >
                  Accept
                </button>
              </div>
              <button
                onClick={declineCookies}
                className="absolute top-2 right-2 md:static p-1 text-white/50 hover:text-white transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
